import React from 'react';
import { Link } from 'react-router-dom';
import { NavNode } from '../../types/NavNode';
import { useUI } from '../../context/UIContext';

interface NavTreeProps {
    nodes: NavNode[];
    /** Slug path the nav should highlight (from useActiveSectionPath). */
    activePath: string[];
    /** Called when a leaf is activated (e.g. the drawer passes closeDrawer). */
    onNavigate?: () => void;
}

// Recursive nav rendered entirely from the config-derived NavNode tree. Branch expansion
// is UI state (useUI); the active trail auto-expands. Styling: uppercase, letter-spaced,
// level indentation, "— " marker on the active top-level item, zero-padded counts on
// gallery leaves.
const NavItem = ({ node, activePath, onNavigate }: { node: NavNode; activePath: string[]; onNavigate?: () => void }) => {
    const { isExpanded, toggleExpanded } = useUI();
    const key = node.slugPath.join('/');
    const activeKey = activePath.join('/');
    const isActive = activeKey === key;
    const containsActive = isActive || activeKey.startsWith(`${key}/`);
    const level = node.slugPath.length - 1;
    const indent = { paddingLeft: level * 18 };
    const marker = level === 0 && containsActive ? '— ' : '';

    if (node.kind === 'branch') {
        const open = isExpanded(key) || containsActive;
        const color = containsActive ? (level === 0 ? 'text-black' : 'text-tone-900') : 'text-tone-800';
        return (
            <li>
                <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => toggleExpanded(key)}
                    style={indent}
                    className={`block w-full py-1 text-left text-caption font-light uppercase tracking-nav ${color} hover:text-black`}
                >
                    {marker}
                    {node.label}
                </button>
                {open && node.children && (
                    <ul>
                        {node.children.map((child) => (
                            <NavItem key={child.slugPath.join('/')} node={child} activePath={activePath} onNavigate={onNavigate} />
                        ))}
                    </ul>
                )}
            </li>
        );
    }

    return (
        <li>
            <Link
                to={node.href ?? '#'}
                onClick={onNavigate}
                aria-current={isActive ? 'page' : undefined}
                style={indent}
                className={`flex items-baseline gap-2 py-1 text-caption font-light uppercase tracking-nav ${isActive ? 'text-black' : 'text-tone-800'} hover:text-black`}
            >
                <span>
                    {marker}
                    {node.label}
                </span>
                {typeof node.count === 'number' && (
                    <span className="text-tone-800">{String(node.count).padStart(2, '0')}</span>
                )}
            </Link>
        </li>
    );
};

export const NavTree = ({ nodes, activePath, onNavigate }: NavTreeProps) => (
    <ul className="leading-[2.4]">
        {nodes.map((node) => (
            <NavItem key={node.slugPath.join('/')} node={node} activePath={activePath} onNavigate={onNavigate} />
        ))}
    </ul>
);
